import {
  DataTypeConfigUtils,
  DataTypeLocator,
  FieldDataTypeConfig,
  QueryNode,
} from '@helo/cms'
import { flow } from '@helo/utils'
import { isEqual } from 'lodash'

import { CmsSlice } from '../cms'
import { QuerySlice } from './querySlice'

export const getQuery =
  (queryId: string) =>
  <T extends QuerySlice>(store: T): QueryNode | undefined =>
    store.queryStore.queries[queryId]

export const findDataTypeConfig = (querySelector: DataTypeLocator) =>
  flow(flow.inputExtends<QuerySlice & CmsSlice>(), store => {
    const queryDefinition = DataTypeConfigUtils.find(querySelector)(
      store.cms.baseDataTypeConfigs,
    )
    if (!queryDefinition) {
      console.error(
        `Query definition with query ${querySelector} not found in queryRegistry.`,
      )
    }
    return queryDefinition as FieldDataTypeConfig
  })

export const setQueryNode =
  (queryNode: QueryNode) =>
  <T extends QuerySlice>(store: T): T => {
    return {
      ...store,
      queryStore: {
        ...store.queryStore,
        queries: {
          ...store.queryStore.queries,
          [queryNode.id]: queryNode,
        },
      },
    }
  }

/**
 * Apply an Apollo success/error QueryNode without clobbering live store state
 * (variables, subscription, flags, etc.). Apollo subscribe callbacks close over
 * the QueryNode from subscription start — spreading that stale node would wipe
 * later filter/variable updates. If the query is absent, insert it.
 */
export const updateQueryNodeResponse =
  (queryNode: QueryNode) =>
  <T extends QuerySlice>(store: T): T => {
    const currentNode = getQuery(queryNode.id)(store)
    if (!currentNode) {
      return setQueryNode(queryNode)(store)
    }

    // Errors only carry apolloState; keep the last good queryContentNode.
    const merged: QueryNode = queryNode.apolloState?.error
      ? {
          ...currentNode,
          apolloState: queryNode.apolloState,
        }
      : {
          ...currentNode,
          apolloState: queryNode.apolloState,
          queryContentNode: queryNode.queryContentNode,
        }

    return setQueryNode(merged)(store)
  }

const paginationKeys = ['after', 'before', 'first', 'last', 'skip', 'take']
const isPaginationKey = ([key]: [string, unknown]) =>
  paginationKeys.includes(key)

const isEmptyValue = (value: unknown): boolean => {
  if (value === undefined) return true
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object' && value !== null) {
    return Object.keys(value).length === 0
  }
  return false
}

const shouldPruneVariable = (
  currentValue: unknown,
  baseValue: unknown,
): boolean => {
  // If current value is undefined, always prune
  if (currentValue === undefined) return true

  // If current equals base (deep equality), prune
  if (isEqual(currentValue, baseValue)) return true

  // If both are empty (undefined, empty array, or empty object), prune
  // Note: null is NOT considered empty - it's a meaningful value that should be kept
  if (isEmptyValue(currentValue) && isEmptyValue(baseValue)) return true

  // Otherwise, keep it (it's different from base)
  return false
}

export const QuerySliceParts = {
  pruneVariables: (
    variables: Record<string, unknown>,
    baseVariables: Record<string, unknown>,
  ) => {
    return Object.fromEntries(
      Object.entries(variables)
        .filter(v => !isPaginationKey(v))
        .filter(
          ([key, value]) => !shouldPruneVariable(value, baseVariables[key]),
        )
        .filter(([key, value]) => {
          // Filter out undefined values (shouldn't happen after shouldPruneVariable, but just in case)
          return value !== undefined
        }),
    )
  },
}
